import { Slot } from '@radix-ui/react-slot';
import { CSSProperties, ElementType, ForwardRefExoticComponent, forwardRef, ReactNode } from 'react';
import { PolymorphicComponentPropWithRef, PolymorphicRef } from '../../utils';
import type { FlexProps, IntrinsicFlexProps } from './flex.component';
import { Flex } from './flex.component';

interface PaddingPure {
	padding: CSSProperties['padding'];
	verticalPadding?: never;
	horizontalPadding?: never;
}
interface PaddingDirectional {
	padding?: never;
	verticalPadding?: CSSProperties['paddingTop'] | CSSProperties['paddingBottom'];
	horizontalPadding?: CSSProperties['paddingLeft'] | CSSProperties['paddingRight'];
}
type Padding = PaddingPure | PaddingDirectional;

type BoxProps<C extends ElementType> = PolymorphicComponentPropWithRef<C, IntrinsicBoxProps>;

type IntrinsicBoxProps = {
	children?: ReactNode;
	/**
	 * Merges its props onto its immediate child.
	 */
	asChild?: boolean;

	gridArea?: CSSProperties['gridArea'];
	width?: CSSProperties['width'];
	height?: CSSProperties['height'];
	borderRadius?: CSSProperties['borderRadius'];
	background?: CSSProperties['backgroundColor'];
	border?: CSSProperties['border'];
	boxShadow?: CSSProperties['boxShadow'];
} & Padding &
	IntrinsicFlexProps;

function CellulaBox<C extends ElementType = 'div'>(
	{
		gridArea,
		padding,
		verticalPadding,
		horizontalPadding,
		asChild,
		width,
		height,
		borderRadius,
		border,
		background,
		boxShadow,
		style,
		...rest
	}: BoxProps<C>,
	ref: PolymorphicRef<C>,
): JSX.Element {
	padding = padding ?? style?.padding;
	padding = padding ? padding : `${verticalPadding ?? '0'} ${horizontalPadding ?? '0'}`;

	const Comp = asChild
		? Slot
		: (Flex as ForwardRefExoticComponent<Omit<FlexProps<ElementType>, 'ref'> & React.RefAttributes<unknown>>);

	return (
		<Comp
			ref={ref}
			gridArea={gridArea}
			style={{
				padding,
				width: width ?? '100%',
				height: height ?? '100%',
				borderRadius,
				border,
				backgroundColor: background,
				boxShadow,
				...style,
			}}
			{...rest}
		/>
	);
}

/**
 * `<Box />` is a general purpose container with Cellula semantics that can be
 * used for custom styling purposes. It supports Cellula tokens to ensure
 * consistency with other Cellula components.
 */
export const Box = forwardRef(
	CellulaBox,
) as typeof CellulaBox; /* This cast is necessary to unwrap the intrinsic types of the polymorphed component */
export type { BoxProps };
